'use client'

import React from 'react'

import { BreadcrumbItem, BreadcrumbItemProps } from './BreadcrumbItem'
import { Container, ScrollContainer } from './styles'

export type { BreadcrumbItemProps } from './BreadcrumbItem'

export interface BreadcrumbsProps {
  /**
   * Content
   */
  children: React.ReactNode
}

/**
 * Render Breadcrumb.Item with `index` and `isLast` prop
 */
const renderItemsWithIndex = (children: React.ReactNode) => {
  const childrenArray = React.Children.toArray(children)
  return childrenArray.map((child, index) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(
        child as React.ReactElement<BreadcrumbItemProps>,
        {
          index,
          isLast: index === childrenArray.length - 1,
        },
      )
    }

    throw new Error('Children of Breadcrumb must be a valid React element')
  })
}

const useItemsWithIndex = (children: React.ReactNode) =>
  React.useMemo(() => renderItemsWithIndex(children), [children])

type BreadcrumbComponent = ((props: BreadcrumbsProps) => React.ReactNode) & {
  Item: typeof BreadcrumbItem
}

/**
 * Breadcrumbs component
 */
export const Breadcrumbs: BreadcrumbComponent = ({
  children,
}: BreadcrumbsProps) => {
  const items = useItemsWithIndex(children)

  return (
    <ScrollContainer>
      <Container itemScope itemType="https://schema.org/BreadcrumbList">
        {items}
      </Container>
    </ScrollContainer>
  )
}

Breadcrumbs.Item = BreadcrumbItem
